import { useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface Suggestion {
  id: string;
  title: string;
  explanation: string;
  confidence: string;
  config_changes: unknown[];
  tradeoffs: string;
  priority: string;
}

interface AdvisorResponse {
  answer: string;
  suggestions: Suggestion[];
}

interface AdvisoryReport {
  overall_health: string;
  executive_summary: string;
  suggestions: Suggestion[];
  risks: string[];
}

export default function Advisor({ session }: Props) {
  const base = `/api/v1/games/${session.gameId}/modules/advisor`;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Ask form
  const [configKey, setConfigKey] = useState('default');
  const [question, setQuestion] = useState('');
  const [lintFindings, setLintFindings] = useState('');
  const [simSummary, setSimSummary] = useState('');
  const [askResult, setAskResult] = useState<AdvisorResponse | null>(null);

  // Report
  const [reportConfigKey, setReportConfigKey] = useState('default');
  const [reportLint, setReportLint] = useState('');
  const [reportSim, setReportSim] = useState('');
  const [report, setReport] = useState<AdvisoryReport | null>(null);

  const askQuestion = async () => {
    if (!question.trim()) return;
    setBusy(true); setError(null);
    try {
      const findings = lintFindings.trim()
        ? lintFindings.split('\n').filter(l => l.trim())
        : [];
      const res = await fetch(`${base}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config_key: configKey,
          question: question.trim(),
          lint_findings: findings,
          sim_summary: simSummary,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setAskResult(data.response);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const generateReport = async () => {
    setBusy(true); setError(null);
    try {
      const findings = reportLint.trim()
        ? reportLint.split('\n').filter(l => l.trim())
        : [];
      const res = await fetch(`${base}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config_key: reportConfigKey,
          lint_findings: findings,
          sim_summary: reportSim,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setReport(data.report);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const priorityColor = (p: string) => {
    if (p === 'critical') return 'badge-warn';
    if (p === 'high') return 'badge-warn';
    return 'badge-muted';
  };

  const healthColor = (grade: string) => {
    if (grade === 'A' || grade === 'B') return 'text-green-400';
    if (grade === 'C') return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <Container>
      <PageHeader
        title="Advisor"
        subtitle="LLM-powered game design recommendations and health reports."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Ask Question */}
      <div className="card p-4 mb-5">
        <h3 className="text-sm font-semibold mb-3">Ask a Question</h3>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Config Key</label>
            <input className="input w-48" value={configKey} onChange={e => setConfigKey(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Question</label>
            <textarea
              className="input w-full"
              rows={3}
              placeholder="e.g. Is the gold economy balanced for casual players?"
              value={question}
              onChange={e => setQuestion(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Lint Findings (one per line, optional)</label>
            <textarea
              className="input w-full font-mono text-xs"
              rows={2}
              value={lintFindings}
              onChange={e => setLintFindings(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Simulation Summary (optional)</label>
            <textarea
              className="input w-full font-mono text-xs"
              rows={2}
              value={simSummary}
              onChange={e => setSimSummary(e.target.value)}
            />
          </div>
          <div>
            <button className="btn btn-primary" disabled={busy || !question.trim()} onClick={askQuestion}>Ask</button>
          </div>
        </div>
        {askResult && (
          <div className="mt-4">
            <div className="card p-3 mb-3 bg-bg-2">
              <div className="text-sm whitespace-pre-wrap">{askResult.answer}</div>
            </div>
            {askResult.suggestions.length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-2">Suggestions</div>
                {askResult.suggestions.map(s => (
                  <div key={s.id} className="card p-3 mb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{s.title}</span>
                      <span className={`badge ${priorityColor(s.priority)}`}>{s.priority}</span>
                      <span className="badge badge-muted">{s.confidence}</span>
                    </div>
                    <div className="text-xs text-fg-2 mb-1">{s.explanation}</div>
                    {s.tradeoffs && <div className="text-xs text-fg-muted">Tradeoffs: {s.tradeoffs}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Generate Report */}
      <div className="card p-4 mb-5">
        <h3 className="text-sm font-semibold mb-3">Generate Full Report</h3>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Config Key</label>
            <input className="input w-48" value={reportConfigKey} onChange={e => setReportConfigKey(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Lint Findings (one per line, optional)</label>
            <textarea
              className="input w-full font-mono text-xs"
              rows={2}
              value={reportLint}
              onChange={e => setReportLint(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Simulation Summary (optional)</label>
            <textarea
              className="input w-full font-mono text-xs"
              rows={2}
              value={reportSim}
              onChange={e => setReportSim(e.target.value)}
            />
          </div>
          <div>
            <button className="btn btn-primary" disabled={busy} onClick={generateReport}>Generate Report</button>
          </div>
        </div>
        {report && (
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs text-fg-muted">Health Grade:</span>
              <span className={`text-2xl font-bold ${healthColor(report.overall_health)}`}>{report.overall_health}</span>
            </div>
            <div className="card p-3 mb-3 bg-bg-2">
              <div className="text-xs font-semibold mb-1">Executive Summary</div>
              <div className="text-sm whitespace-pre-wrap">{report.executive_summary}</div>
            </div>
            {report.suggestions.length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-semibold mb-2">Suggestions</div>
                {report.suggestions.map(s => (
                  <div key={s.id} className="card p-3 mb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{s.title}</span>
                      <span className={`badge ${priorityColor(s.priority)}`}>{s.priority}</span>
                      <span className="badge badge-muted">{s.confidence}</span>
                    </div>
                    <div className="text-xs text-fg-2 mb-1">{s.explanation}</div>
                    {s.tradeoffs && <div className="text-xs text-fg-muted">Tradeoffs: {s.tradeoffs}</div>}
                  </div>
                ))}
              </div>
            )}
            {report.risks.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-red-400 mb-1">Risks</div>
                {report.risks.map((r, i) => (
                  <div key={i} className="text-xs text-red-300">{r}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Container>
  );
}
